<script lang="ts">
import type { AsTag } from 'reka-ui';

/**
 * Three pulsing dots over a status message, with a secondary line that appears
 * only once the wait has gone on long enough to be worth explaining.
 *
 * Use it for waits the user is actively staring at — a payment being processed,
 * a bank redirect being prepared. For a plain indeterminate spinner with no
 * message, use `TSpinner`; for the legacy full-page overlay, `TLoading`.
 *
 * ## Layout is the consumer's job
 *
 * The component paints dots and centered text and nothing else — no backdrop, no
 * `position`, no `z-index`. Center it in a card or stretch it over a screen from
 * the outside. That is deliberate: the design renders identically inside a
 * desktop card and on a full-screen phone, so the surface belongs to whoever
 * mounts it.
 *
 * ## Announcements
 *
 * The root is a `role='status'` live region, so `message` is announced politely
 * and the dots are hidden from assistive tech.
 *
 * `hint` is deliberately announced too — "this is taking a while" is information a
 * screen reader user cannot get from watching the dots. Getting that *and* a
 * shift-free reveal takes some care: the line is in the DOM from mount so its box is
 * reserved, but while pending it is `aria-hidden` and `visibility: hidden`, so it is
 * silent and invisible. On reveal it is re-inserted as a fresh node (hence the `key`),
 * because a DOM insertion into a live region is the announcement trigger that
 * assistive tech supports most consistently — a visibility-only flip is not.
 *
 * Used bare, with no `message`, the region has nothing to announce: the dots are
 * decoration and hidden from assistive tech, so the loader is silent. That is fine
 * when something nearby already says what is happening; when it does not, pass an
 * `aria-label` — it falls through to the element carrying the role.
 */
export interface TLoadingDotsProps {
  /** Primary line under the dots. Also what the live region announces. */
  message?: string;

  /**
   * Secondary line, revealed after `hintDelay`. Meant for reassurance on a slow
   * wait ("Esto puede tardar unos segundos…"), not for status that must be read
   * immediately — anything urgent belongs in `message`.
   */
  hint?: string;

  /**
   * Milliseconds to wait before revealing `hint`. `0` shows it right away;
   * `false` never reveals it, which keeps `hint` addressable by a parent that
   * decides on its own when the wait became long.
   * @defaultValue 5000
   */
  hintDelay?: number | false;

  /**
   * Element the wrapper renders as. `'div'` suits a block of page; `'output'`
   * is worth considering when the loader reports the result of a form the user
   * just submitted. Every element this component renders inside the wrapper is
   * phrasing content, so `'output'` and other phrasing-only tags stay valid —
   * keep that in mind before swapping one for a block element in the slots.
   * @defaultValue 'div'
   */
  as?: AsTag;
}

export interface TLoadingDotsSlots {
  /** Replaces the `message` line — for rich content such as an inline amount. */
  message?: () => unknown;
  /** Replaces the `hint` line. Still governed by `hintDelay`. */
  hint?: () => unknown;
}
</script>

<script setup lang="ts">
import { Primitive } from 'reka-ui';
import { computed, onUnmounted, ref, watch } from 'vue';

defineOptions({ name: 'TLoadingDots' });

const {
  message,
  hint,
  hintDelay = 5000,
  as = 'div',
} = defineProps<TLoadingDotsProps>();

const slots = defineSlots<TLoadingDotsSlots>();

const hintElapsed = ref(false);

// A bare timeout rather than VueUse's useTimeoutFn: one setTimeout does not justify
// a utility library in the runtime dependencies of a published package, and ADR 006
// leaves that trade to the caller.
let timer: ReturnType<typeof setTimeout> | undefined;

const stop = () => {
  clearTimeout(timer);
  timer = undefined;
};

const start = (delay: number) => {
  timer = setTimeout(() => {
    hintElapsed.value = true;
  }, delay);
};

onUnmounted(stop);

// Re-arm on every `hintDelay` change so a parent can restart the reveal, and so
// switching to `false` retracts a hint that is already on screen.
watch(
  () => hintDelay,
  (delay) => {
    stop();
    hintElapsed.value = false;
    if (delay !== false) {
      start(delay);
    }
  },
  { immediate: true },
);

// The hint is in the DOM from mount and only *hidden* until the delay elapses, so
// revealing it cannot shift the block: its exact box, wrapping included, is reserved
// up front. Skipped entirely when `hintDelay` is false, where the line is never going
// to appear and reserving space for it would be dead air.
const hintReserved = computed(() => Boolean(hint || slots.hint) && hintDelay !== false);
const messageShown = computed(() => Boolean(message || slots.message));
</script>

<template>
  <Primitive
    :as
    class="t-loading-dots"
    role="status"
    aria-live="polite"
  >
    <!-- Everything below is phrasing content so a phrasing-only `as` (e.g. `output`) stays valid HTML. -->
    <span class="t-loading-dots__dots" aria-hidden="true">
      <span class="t-loading-dots__dot" />
      <span class="t-loading-dots__dot" />
      <span class="t-loading-dots__dot" />
    </span>

    <span v-if="messageShown || hintReserved" class="t-loading-dots__text">
      <span v-if="messageShown" class="t-loading-dots__message">
        <slot name="message">
          {{ message }}
        </slot>
      </span>
      <span
        v-if="hintReserved"
        :key="hintElapsed ? 'shown' : 'pending'"
        class="t-loading-dots__hint"
        :data-pending="!hintElapsed || undefined"
        :aria-hidden="!hintElapsed || undefined"
      >
        <slot name="hint">
          {{ hint }}
        </slot>
      </span>
    </span>
  </Primitive>
</template>

<style scoped lang="scss">
// px, not rem: the dots are a fixed graphic, 10px in Figma at every breakpoint, and
// should not grow with the root font size the way the copy below them should.
$dot-size: 10px;

// Matches the dot. Deliberately off the --space-* scale, which has no 10px step;
// Figma measures 12px here and this is a considered override of it.
$dot-gap: 10px;
$dot-rest-scale: 0.6;

// The cycle is split into 7 so consecutive dots start 200ms apart at the default
// 1400ms period, and stays proportional if the period is retuned.
$stagger-steps: 7;

// Figma wraps both lines somewhere between 276px and 308px, identically on mobile
// and inside the desktop card. 288px sits in that band; `min()` keeps it from
// overflowing a narrow phone once the consumer adds its own padding.
$text-max-width: 18rem;

.t-loading-dots {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  font-family: var(--font-family-base);
  text-align: center;

  &__dots {
    display: flex;
    align-items: center;
    gap: $dot-gap;
  }

  &__dot {
    display: block;
    width: $dot-size;
    height: $dot-size;
    border-radius: var(--radius-pill-default);

    // The pulse swaps between two steps of the gray alpha ramp rather than dialling
    // `opacity` on a solid colour: an opacity multiplier invents shades that are not in
    // the palette, whereas both ends here are named tokens. gray-a-11 is the alpha twin
    // of gray-11, so the lit dot is the same colour it always was.
    background-color: var(--color-gray-a-11);

    // Base style is the lit dot, so anything that suppresses the animation leaves three
    // solid dots rather than a dimmed, shrunken row.
    transform: scale(1);
    animation: t-loading-dots-pulse var(--motion-loading-duration) var(--motion-loading-easing) infinite;

    // Negative delays start the row mid-cycle, so it mounts already in motion.
    &:nth-child(1) {
      animation-delay: calc(var(--motion-loading-duration) / #{$stagger-steps} * -2);
    }

    &:nth-child(2) {
      animation-delay: calc(var(--motion-loading-duration) / #{$stagger-steps} * -1);
    }

    // Collapsing the cycle to zero stops the pulse and, with no fill mode, leaves
    // the dots on their visible base style instead of a dimmed keyframe — verified
    // in-browser. The message keeps announcing, so the loader still reads as active.
    @media (prefers-reduced-motion: reduce) {
      animation-duration: var(--motion-duration-instant);
      animation-iteration-count: 1;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-1);
    max-width: min(#{$text-max-width}, 100%);

    // max-width alone caps the box but creates no break opportunity, so a single
    // unbroken run (a URL, an id) would spill past it. Inherited by both lines.
    overflow-wrap: anywhere;
  }

  &__message {
    font-size: var(--font-b3-font-size);
    line-height: var(--font-b3-line-height);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-default-default);
  }

  &__hint {
    font-size: var(--font-b4-font-size);
    line-height: var(--font-b4-line-height);
    font-weight: var(--font-weight-regular);

    // Figma binds both lines to neutral/text-contrast — the hint reads lighter
    // because it is 12px regular, not because it is a muted color.
    color: var(--color-text-default-default);

    // An animation, not a transition: on reveal this is a freshly inserted node, and a
    // new node has no previous state to transition from — an animation still runs.
    &:not([data-pending]) {
      animation: t-loading-dots-hint-in var(--motion-enter-duration) var(--motion-enter-easing);

      @media (prefers-reduced-motion: reduce) {
        animation-duration: var(--motion-duration-instant);
      }
    }

    // Holds the line's box before the delay elapses so the reveal costs no layout
    // shift, while staying invisible and out of the accessibility tree.
    &[data-pending] {
      opacity: 0;
      visibility: hidden;
    }
  }
}

@keyframes t-loading-dots-pulse {
  0%,
  80%,
  100% {
    background-color: var(--color-gray-a-7);
    transform: scale($dot-rest-scale);
  }

  40% {
    background-color: var(--color-gray-a-11);
    transform: scale(1);
  }
}

@keyframes t-loading-dots-hint-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}
</style>
